<template>
  <v-container fluid>
    <v-row no-gutters class="d-flex justify-space-between align-center mt-10 mb-4">
      <h1 class="page-title mb-0">Portfolio Categories</h1>
      <v-btn
        @click="openAddDialog"
        color="secondary"
        class="text-capitalize button-shadow"
      >
        <v-icon left small>mdi-plus</v-icon>
        Add Category
      </v-btn>
    </v-row>

    <div class="tables-basic">
      <v-row>
        <v-col cols="12">
          <v-card class="employee-list mb-1">
            <v-card-title class="pa-6 pb-3">
              <p>Categories</p>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="categories_table.search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                clearable
              ></v-text-field>
            </v-card-title>
            <v-data-table
              :headers="categories_table.headers"
              :items="categories_table.categories"
              :search="categories_table.search"
              :loading="isLoading"
              loading-text="Loading categories..."
              no-data-text="No categories found. Click 'Add Category' to create one."
              item-key="id"
            >
              <template v-slot:[`item.type`]="{ item }">
                <v-chip
                  small
                  :color="getTypeColor(item.type)"
                  text-color="white"
                >
                  {{ item.type | formatType }}
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      small
                      class="mr-3"
                      color="primary"
                      v-on="on"
                      @click="openEditDialog(item)"
                    >
                      mdi-pencil
                    </v-icon>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      small
                      color="error"
                      v-on="on"
                      @click="confirmDelete(item)"
                    >
                      mdi-delete
                    </v-icon>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Add/Edit Category Dialog -->
    <v-dialog v-model="showFormDialog" persistent max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isEditing ? 'Edit Category' : 'Add Category' }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  label="Name"
                  v-model="form.name"
                  :error-messages="formErrors.name"
                  outlined
                  dense
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-select
                  :items="types"
                  item-text="name"
                  item-value="id"
                  label="Type"
                  v-model="form.type"
                  :error-messages="formErrors.type"
                  outlined
                  dense
                  required
                ></v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeFormDialog"
            :disabled="formLoading"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="formLoading"
            @click="saveCategory"
          >
            {{ isEditing ? 'Update' : 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="450px">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete <strong>{{ toDeleteName }}</strong>?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="showDeleteDialog = false"
            :disabled="deleteLoading"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :loading="deleteLoading"
            @click="deletePortfolioCategory"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "PortfolioCategories",
  data() {
    return {
      isLoading: false,
      categories_table: {
        categories: [],
        search: "",
        headers: [
          {
            text: "Name",
            align: "start",
            sortable: true,
            value: "name",
          },
          { text: "Type", value: "type" },
          { text: "Actions", value: "actions", sortable: false, align: "end" },
        ],
      },
      showFormDialog: false,
      showDeleteDialog: false,
      isEditing: false,
      editingId: null,
      formLoading: false,
      deleteLoading: false,
      toDelete: "",
      toDeleteName: "",
      form: {
        name: "",
        type: "",
      },
      formErrors: {
        name: [],
        type: [],
      },
      types: [
        { name: "Image", id: 1 },
        { name: "Video", id: 2 },
        { name: "Website", id: 3 },
      ],
    };
  },
  methods: {
    getCategories() {
      this.isLoading = true;
      axios.get("portfolio-categories")
        .then(({ data }) => {
          this.categories_table.categories = data;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getTypeColor(type) {
      switch (type) {
        case 1: return "primary";
        case 2: return "warning";
        case 3: return "success";
        default: return "grey";
      }
    },
    openAddDialog() {
      this.isEditing = false;
      this.editingId = null;
      this.form = { name: "", type: "" };
      this.formErrors = { name: [], type: [] };
      this.showFormDialog = true;
    },
    openEditDialog(item) {
      this.isEditing = true;
      this.editingId = item.id;
      this.form = { name: item.name, type: item.type };
      this.formErrors = { name: [], type: [] };
      this.showFormDialog = true;
    },
    closeFormDialog() {
      this.showFormDialog = false;
      this.form = { name: "", type: "" };
      this.formErrors = { name: [], type: [] };
    },
    validateForm() {
      this.formErrors = { name: [], type: [] };
      let valid = true;
      if (!this.form.name || !this.form.name.trim()) {
        this.formErrors.name = ["Name is required"];
        valid = false;
      }
      if (!this.form.type) {
        this.formErrors.type = ["Type is required"];
        valid = false;
      }
      return valid;
    },
    saveCategory() {
      if (!this.validateForm()) return;

      this.formLoading = true;
      const payload = { name: this.form.name.trim(), type: this.form.type };

      const request = this.isEditing
        ? axios.put("portfolio-categories/" + this.editingId, payload)
        : axios.post("portfolio-categories", payload);

      request
        .then(() => {
          this.closeFormDialog();
          this.getCategories();
          this.$toast.success(this.isEditing ? "Category updated successfully" : "Category added successfully");
        })
        .catch((error) => {
          if (error.response && error.response.data && error.response.data.errors) {
            const errors = error.response.data.errors;
            if (errors.name) this.formErrors.name = errors.name;
            if (errors.type) this.formErrors.type = errors.type;
          } else {
            this.$toast.error("Something went wrong. Please try again.");
          }
        })
        .finally(() => {
          this.formLoading = false;
        });
    },
    confirmDelete(item) {
      this.toDelete = item.id;
      this.toDeleteName = item.name;
      this.showDeleteDialog = true;
    },
    deletePortfolioCategory() {
      this.deleteLoading = true;
      axios
        .delete("portfolio-categories/" + this.toDelete)
        .then(() => {
          this.getCategories();
          this.showDeleteDialog = false;
          this.$toast.success("Category deleted successfully");
        })
        .catch(() => {
          this.$toast.error("Failed to delete category. It may have portfolio items.");
        })
        .finally(() => {
          this.deleteLoading = false;
        });
    },
  },
  filters: {
    formatType: function(value) {
      switch (value) {
        case 1: return "Image";
        case 2: return "Video";
        case 3: return "Website";
        default: return "Unknown";
      }
    },
  },
  mounted() {
    this.getCategories();
  },
};
</script>

<style src="./PortfolioCategories.scss" lang="scss"></style>
